/**
 * `<xa-line>` — line chart (default variant A "classic with points").
 * Registered as `xaLine`. Mousemoves anywhere on the SVG snap a dashed
 * crosshair + tooltip to the nearest category x; hovered dots grow to 4.5px
 * (or appear at r=4 when `dots` is off — one per visible series).
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaCategorical` | — | required |
 * | `colors` | `XaPalette` | `'brand'` | global `xaChartsConfig.palette` wins over the default |
 * | `width` | `number` | `520` | SVG px; ignored when `auto-width` is on |
 * | `auto-width` | `boolean` | `false` | re-flow to the container width (responsive) |
 * | `height` | `number` | `260` | SVG px |
 * | `smooth` | `boolean` | `false` | Catmull-Rom → cubic-Bézier smoothing |
 * | `dots` | `boolean` | `true` | 2.75px r, white fill, 2px series stroke |
 * | `legend` | `'top' \| 'bottom' \| 'none'` | `'bottom'` | |
 * | `legend-values` | `boolean` | `false` | per-series totals; bottom + values = column layout |
 * | `end-labels` | `boolean` | `false` | `name · fmt(last)` at line end (reserves ~110px right padding) |
 * | `max-x-labels` | `number` | `8` | x-label thinning |
 * | `area` | `'none' \| 'gradient' \| 'flat'` | `'none'` | internal — set by `<xa-area>` |
 * | `stacked` | `boolean` | `false` | internal — set by `<xa-area>` |
 * | `on-series-toggle` | `&` callback | — | `$event: XaSeriesToggleEvent` |
 * | `on-point-hover` | `&` callback | — | `$event: XaHoverEvent` |
 */
import {
  categoricalAriaLabel,
  computeLine,
  CROSSHAIR_DASH,
  CROSSHAIR_STROKE,
  DOT_RADIUS,
  DOT_RADIUS_APPEAR,
  DOT_RADIUS_HOVER,
  ensureChartStyles,
  GRID_DASH,
  legendLayoutFor,
  lineTooltip,
  nearestIndex,
  normalizeCategorical,
} from '@xait/charts-core';
import type {
  XaAreaMode,
  XaCategorical,
  XaHoverEvent,
  XaLegendLayout,
  XaLegendPos,
  XaLineLayout,
  XaPalette,
  XaSeriesToggleEvent,
} from '@xait/charts-core';
import {
  nextGradientSalt,
  XaChartBaseController,
  XAC_CATEGORICAL_TABLE,
  XAC_TIP_TEMPLATE,
} from './chart-base';
import type { XaCallback } from './chart-base';
import type { XaChartsDefaults } from './config';

class XaLineController extends XaChartBaseController {
  static override $inject = ['$element', '$scope', 'xaChartsConfig'];

  /* bindings */
  data?: XaCategorical;
  colors?: XaPalette;
  width?: number;
  height?: number;
  smooth?: boolean;
  dots?: boolean;
  legend?: XaLegendPos;
  legendValues?: boolean;
  endLabels?: boolean;
  maxXLabels?: number;
  rotateXLabels?: boolean;
  /**
   * Enable mouse-wheel X-axis zoom + native horizontal scrollbar.
   * AngularJS attribute is `zoom-x` (not `x-zoom`): AngularJS strips a leading
   * `x-`/`data-` prefix during attribute normalization, so `x-zoom` would map to
   * the `zoom` binding and never reach this one.
   */
  zoomX?: boolean;
  /** Maximum X-axis zoom factor when `zoomX` is on (default 8). */
  maxZoom?: number;
  /** Internal (used by `<xa-area>`): area fill mode. */
  area?: XaAreaMode;
  /** Internal (used by `<xa-area>`): stack the series. */
  stacked?: boolean;
  onSeriesToggle!: XaCallback<XaSeriesToggleEvent>;
  onPointHover!: XaCallback<XaHoverEvent>;

  /* view state */
  layout: XaLineLayout | null = null;
  legendPos: XaLegendPos = 'bottom';
  legendLayout: XaLegendLayout = 'row';
  legendValuesOn = false;
  dotsOn = true;
  areaMode: XaAreaMode = 'none';
  hoverIndex: number | null = null;
  ariaLabel = '';

  /** Current X-axis zoom factor (1 = fit to `width`); driven by the wheel when `zoomX`. */
  private zoom = 1;
  /** The horizontal scroll container element (resolved in `$postLink`). */
  private scrollEl: HTMLElement | null = null;
  /** Bound wheel listener, kept for removal in `$onDestroy`. */
  private wheelHandler: ((ev: WheelEvent) => void) | null = null;

  private readonly hidden = new Set<string>();
  /** Unique per instance, so SVG gradient ids of multiple charts never collide. */
  private readonly salt = nextGradientSalt();

  constructor(
    $element: ng.IAugmentedJQuery,
    private readonly $scope: ng.IScope,
    private readonly xaChartsConfig: XaChartsDefaults,
  ) {
    super($element);
  }

  $onInit(): void {
    ensureChartStyles();
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  override $postLink(): void {
    super.$postLink();
    // Bind the wheel listener on the always-present host element rather than
    // the inner `.xac-scroll-x` (which may not exist yet under `ng-if`/lazy
    // views in a bundled host). The handler self-guards on `zoomX`/`shiftKey`
    // and re-resolves the scroll container lazily, so binding here is safe and
    // resilient to mount timing. Non-passive so `onWheel` can `preventDefault`.
    this.wheelHandler = (ev: WheelEvent) => this.onWheel(ev);
    this.$element[0].addEventListener('wheel', this.wheelHandler, { passive: false });
    this.observeAutoWidth(() => this.$scope.$applyAsync(() => this.recompute()));
  }

  $onDestroy(): void {
    if (this.wheelHandler) {
      this.$element[0].removeEventListener('wheel', this.wheelHandler);
    }
    this.wheelHandler = null;
    this.disconnectAutoWidth();
  }

  /** The horizontal scroll container, resolved lazily (lives under the plot wrapper). */
  private scroller(): HTMLElement | null {
    if (!this.scrollEl || !this.scrollEl.isConnected) {
      this.scrollEl = this.$element[0].querySelector('.xac-scroll-x') as HTMLElement | null;
    }
    return this.scrollEl;
  }

  /** The live `<svg>` element (re-queried because it lives under `ng-if`). */
  private svg(): SVGSVGElement | null {
    return this.$element[0].querySelector('svg.xac-svg') as SVGSVGElement | null;
  }

  /**
   * Wheel over the plot when `zoomX` is on **and Shift is held**: zoom the X
   * axis in/out around the cursor (wheel up stretches the axis, wheel down
   * shrinks toward fit), clamped to `[1, maxZoom]`, then keep the category
   * under the cursor pinned by adjusting the scroll position. Without Shift the
   * wheel is ignored so normal page scrolling is unaffected.
   */
  private onWheel(ev: WheelEvent): void {
    if (!this.zoomX || !this.layout || !ev.shiftKey) return;
    ev.preventDefault();
    const prev = this.zoom;
    // Shift+wheel often reports horizontal deltaX; honor whichever axis moved.
    const delta = ev.deltaY !== 0 ? ev.deltaY : ev.deltaX;
    const factor = delta < 0 ? 1.15 : 1 / 1.15;
    const next = Math.min(Math.max(prev * factor, 1), Math.max(this.maxZoom ?? 8, 1));
    if (next === prev) return;
    const scroller = this.scroller();
    const host = this.svg() ?? scroller;
    const cursorContentX = host ? ev.clientX - host.getBoundingClientRect().left : 0;
    this.zoom = next;
    this.$scope.$apply(() => this.recompute());
    if (scroller) {
      const ratio = next / prev;
      const viewportX = ev.clientX - scroller.getBoundingClientRect().left;
      scroller.scrollLeft = cursorContentX * ratio - viewportX;
    }
  }

  toggleSeries(name: string): void {
    const nowHidden = !this.hidden.delete(name);
    if (nowHidden) this.hidden.add(name);
    this.recompute();
    this.onSeriesToggle({ $event: { name, hidden: nowHidden, hiddenAll: Array.from(this.hidden) } });
  }

  isolateSeries(name: string): void {
    const all = (this.layout?.legend ?? []).map((it) => it.label);
    const isolated =
      all.length > 1 && !this.hidden.has(name) && all.every((n) => n === name || this.hidden.has(n));
    this.hidden.clear();
    if (!isolated) {
      for (const n of all) if (n !== name) this.hidden.add(n);
    }
    this.recompute();
    this.onSeriesToggle({
      $event: { name, hidden: this.hidden.has(name), hiddenAll: Array.from(this.hidden) },
    });
  }

  moveCrosshair(ev: MouseEvent): void {
    if (!this.layout || !this.data || !this.layout.xs.length) return;
    const p = this.pointerPos(ev);
    const i = nearestIndex(this.layout.xs, p.x);
    this.showTip(ev, lineTooltip(this.layout, this.data, i));
    if (this.hoverIndex !== i) {
      this.hoverIndex = i;
      this.onPointHover({ $event: { categoryIndex: i, category: this.data.categories[i] } });
    }
  }

  /**
   * Pointer X in plot space. Measured against the `<svg>` element (whose left
   * edge reflects the horizontal scroll offset when `zoomX`) so the crosshair
   * stays accurate while scrolled; falls back to the container otherwise.
   */
  protected override pointerPos(ev: MouseEvent): { x: number; y: number } {
    const svg = this.svg();
    if (svg) {
      const rect = svg.getBoundingClientRect();
      return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
    }
    return super.pointerPos(ev);
  }

  leaveCrosshair(): void {
    this.hideTip();
    if (this.hoverIndex !== null) {
      this.hoverIndex = null;
      this.onPointHover({ $event: { categoryIndex: null } });
    }
  }

  private recompute(): void {
    if (this.data) {
      this.data = normalizeCategorical(this.data);
    }
    this.legendPos = this.legend ?? 'bottom';
    this.legendValuesOn = this.legendValues ?? false;
    this.legendLayout = legendLayoutFor('line', this.legendPos, this.legendValuesOn);
    this.dotsOn = this.dots ?? true;
    this.areaMode = this.area ?? 'none';
    if (!this.data) {
      this.layout = null;
      return;
    }
    if (this.hoverIndex !== null && this.hoverIndex >= this.data.categories.length) {
      this.hoverIndex = null;
    }
    // Keep the zoom factor within [1, maxZoom] (maxZoom may change at runtime).
    this.zoom = Math.min(Math.max(this.zoom, 1), Math.max(this.maxZoom ?? 8, 1));
    const baseWidth = this.effectiveWidth(this.width ?? 520);
    this.layout = computeLine(this.data, {
      width: baseWidth,
      height: this.height ?? 260,
      contentWidth: this.zoomX ? Math.round(baseWidth * this.zoom) : undefined,
      smooth: this.smooth ?? false,
      endLabels: this.endLabels ?? false,
      maxXLabels: this.maxXLabels ?? 8,
      rotateXLabels: this.rotateXLabels ?? false,
      colors: this.colors ?? this.xaChartsConfig.palette ?? 'brand',
      hidden: this.hidden,
      stacked: this.stacked ?? false,
      area: this.areaMode,
      gradientSalt: this.salt,
    });
    this.ariaLabel = categoricalAriaLabel(this.areaMode === 'none' ? 'Line' : 'Area', this.data);
  }
}

export const xaLineComponent: ng.IComponentOptions = {
  controller: XaLineController,
  bindings: {
    data: '<',
    colors: '<',
    width: '<',
    autoWidth: '<',
    height: '<',
    smooth: '<',
    dots: '<',
    legend: '<',
    legendValues: '<',
    endLabels: '<',
    maxXLabels: '<',
    rotateXLabels: '<',
    zoomX: '<',
    maxZoom: '<',
    area: '<',
    stacked: '<',
    onSeriesToggle: '&',
    onPointHover: '&',
  },
  template: `<div class="xac-chart">
  <xac-legend ng-if="$ctrl.layout && $ctrl.legendPos === 'top'" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="$ctrl.legendValuesOn" on-toggle="$ctrl.toggleSeries(label)" on-isolate="$ctrl.isolateSeries(label)"></xac-legend>
  <div class="xac-plot-wrap">
  <div class="xac-scroll-x" ng-style="$ctrl.zoomX && $ctrl.layout ? { width: $ctrl.layout.viewWidth + 'px', overflowX: 'auto', paddingBottom: $ctrl.layout.width > $ctrl.layout.viewWidth ? '14px' : '0' } : { overflowX: 'visible' }">
  <svg ng-if="$ctrl.layout" class="xac-svg" role="img" aria-label="{{$ctrl.ariaLabel}}" ng-attr-width="{{$ctrl.layout.width}}" ng-attr-height="{{$ctrl.layout.height}}" ng-mousemove="$ctrl.moveCrosshair($event)" ng-mouseleave="$ctrl.leaveCrosshair()">
    <defs ng-if="$ctrl.areaMode === 'gradient'">
      <linearGradient ng-repeat="s in $ctrl.layout.seriesOut track by $index" ng-attr-id="{{s.gradientId}}" x1="0" y1="0" x2="0" y2="1">
        <stop ng-attr-offset="{{s.gradientStops[0].offset}}" ng-attr-stop-color="{{s.color}}" ng-attr-stop-opacity="{{s.gradientStops[0].opacity}}"></stop>
        <stop ng-attr-offset="{{s.gradientStops[1].offset}}" ng-attr-stop-color="{{s.color}}" ng-attr-stop-opacity="{{s.gradientStops[1].opacity}}"></stop>
      </linearGradient>
    </defs>
    <g ng-repeat="g in $ctrl.layout.grid track by $index">
      <line ng-attr-x1="{{g.x0}}" ng-attr-x2="{{g.x1}}" ng-attr-y1="{{g.y}}" ng-attr-y2="{{g.y}}" ng-attr-stroke="{{g.stroke}}" ng-attr-stroke-dasharray="{{g.baseline ? undefined : '${GRID_DASH}'}}"></line>
      <text class="xac-axis" text-anchor="end" ng-attr-x="{{g.labelX}}" ng-attr-y="{{g.labelY}}">{{g.label}}</text>
    </g>
    <text ng-repeat="l in $ctrl.layout.xLabels track by $index" class="xac-axis" ng-attr-text-anchor="{{l.rotate ? 'end' : 'middle'}}" ng-attr-x="{{l.x}}" ng-attr-y="{{l.y}}" ng-attr-transform="{{l.rotate ? 'rotate(' + l.rotate + ' ' + l.x + ' ' + l.y + ')' : undefined}}">{{l.text}}</text>
    <line ng-if="$ctrl.hoverIndex != null" stroke="${CROSSHAIR_STROKE}" stroke-dasharray="${CROSSHAIR_DASH}" ng-attr-x1="{{$ctrl.layout.xs[$ctrl.hoverIndex]}}" ng-attr-x2="{{$ctrl.layout.xs[$ctrl.hoverIndex]}}" ng-attr-y1="{{$ctrl.layout.plotTop}}" ng-attr-y2="{{$ctrl.layout.plotBottom}}"></line>
    <g ng-repeat="s in $ctrl.layout.seriesOut track by $index">
      <path ng-if="s.areaPath" stroke="none" ng-attr-d="{{s.areaPath}}" ng-attr-fill="{{$ctrl.areaMode === 'gradient' ? 'url(#' + s.gradientId + ')' : s.color}}" ng-attr-opacity="{{s.areaOpacity}}"></path>
      <path fill="none" stroke-width="2.25" stroke-linejoin="round" stroke-linecap="round" ng-attr-d="{{s.linePath}}" ng-attr-stroke="{{s.color}}"></path>
      <g ng-if="$ctrl.dotsOn">
        <circle ng-repeat="p in s.points track by $index" fill="#fff" stroke-width="2" ng-attr-cx="{{p.x}}" ng-attr-cy="{{p.y}}" ng-attr-r="{{$ctrl.hoverIndex === $index ? ${DOT_RADIUS_HOVER} : ${DOT_RADIUS}}}" ng-attr-stroke="{{s.color}}"></circle>
      </g>
      <circle ng-if="!$ctrl.dotsOn && $ctrl.hoverIndex != null" fill="#fff" stroke-width="2" r="${DOT_RADIUS_APPEAR}" ng-attr-cx="{{s.points[$ctrl.hoverIndex].x}}" ng-attr-cy="{{s.points[$ctrl.hoverIndex].y}}" ng-attr-stroke="{{s.color}}"></circle>
      <text ng-if="s.endLabel" class="xac-endlabel" ng-attr-x="{{s.endLabel.x}}" ng-attr-y="{{s.endLabel.y}}" ng-attr-fill="{{s.color}}">{{s.endLabel.text}}</text>
    </g>
  </svg>
  </div>
  <svg ng-if="$ctrl.zoomX && $ctrl.layout" class="xac-yaxis" aria-hidden="true" ng-attr-width="{{$ctrl.layout.gutter}}" ng-attr-height="{{$ctrl.layout.height}}">
    <rect x="0" y="0" ng-attr-width="{{$ctrl.layout.axisMaskWidth}}" ng-attr-height="{{$ctrl.layout.height}}" fill="var(--xa-surface, #fff)"></rect>
    <text ng-repeat="g in $ctrl.layout.grid track by $index" class="xac-axis" text-anchor="end" ng-attr-x="{{g.labelX}}" ng-attr-y="{{g.labelY}}">{{g.label}}</text>
  </svg>
  </div>
  <xac-legend ng-if="$ctrl.layout && $ctrl.legendPos === 'bottom'" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="$ctrl.legendValuesOn" on-toggle="$ctrl.toggleSeries(label)" on-isolate="$ctrl.isolateSeries(label)"></xac-legend>
  ${XAC_CATEGORICAL_TABLE}
  ${XAC_TIP_TEMPLATE}
</div>`,
};
